import { Heart } from 'lucide-react';
import { useFilterStore } from '../../stores/filterStore';
import type { StarFilter, UserFilter } from '../../api/types';

const STAR_OPTIONS: { value: StarFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '1+', label: '1+' },
  { value: '2+', label: '2+' },
  { value: '3+', label: '3+' },
  { value: '4+', label: '4+' },
  { value: '5', label: '5' },
];

// "By"-checkboxar: Henrik och Michelle aktiveras oberoende.
//   ingen aktiv  → user='all'  (någon av oss = OR)
//   bara Henrik  → user='henrik'
//   bara Michelle → user='michelle'
//   båda aktiva  → user='both' (båda = AND)
const BY_USERS = [
  { name: 'henrik' as const, label: 'Henrik', activeClass: 'bg-henrik text-white border-henrik' },
  { name: 'michelle' as const, label: 'Michelle', activeClass: 'bg-michelle text-white border-michelle' },
];

function deriveUserFilter(henrik: boolean, michelle: boolean): UserFilter {
  if (henrik && michelle) return 'both';
  if (henrik) return 'henrik';
  if (michelle) return 'michelle';
  return 'all';
}

interface ColumnIconProps {
  count: 1 | 2 | 3 | 4;
}
function ColumnIcon({ count }: ColumnIconProps) {
  const widths: Record<number, number[]> = {
    1: [10],
    2: [5, 5],
    3: [4, 4, 4],
    4: [3, 3, 3, 3],
  };
  const w = widths[count] ?? widths[3]!;
  let x = 1;
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
      {w.map((width, i) => {
        const rect = <rect key={i} x={x} y={2} width={width} height={12} rx="0.5" />;
        x += width + 1;
        return rect;
      })}
    </svg>
  );
}

interface FilterBarProps {
  /** Ytterligare CSS för positionering (Gallery sätter sticky-top + auto-hide). */
  className?: string;
}

// Komplett filterbar för gallery-vyn.
// På mobil: scrollar horisontellt.
export function FilterBar({ className = '' }: FilterBarProps) {
  const { stars, user, favorites, columns, setStars, setUser, toggleFavorites, setColumns } =
    useFilterStore();

  // Derivera checkbox-state från det enklare user-värdet
  const henrikActive = user === 'henrik' || user === 'both';
  const michelleActive = user === 'michelle' || user === 'both';

  function toggleByUser(name: 'henrik' | 'michelle') {
    if (name === 'henrik') {
      setUser(deriveUserFilter(!henrikActive, michelleActive));
    } else {
      setUser(deriveUserFilter(henrikActive, !michelleActive));
    }
  }

  return (
    <div
      className={[
        'bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border',
        'px-4 sm:px-7 py-3',
        'flex items-center gap-3 overflow-x-auto scrollbar-none',
        className,
      ].join(' ')}
    >
      {/* Stars */}
      <div className="flex items-center gap-2 pr-3.5 border-r border-loupe-border shrink-0">
        <span className="hidden sm:inline text-[10px] tracking-[0.15em] uppercase text-loupe-text-3">
          Stars
        </span>
        <div className="flex gap-px p-1 bg-loupe-bg-2 border border-loupe-border rounded-lg">
          {STAR_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setStars(o.value)}
              className={[
                'px-2.5 py-1.5 text-xs rounded-md font-mono tabular-nums min-h-[32px] transition-colors',
                stars === o.value
                  ? 'text-gold bg-gold/10'
                  : 'text-loupe-text-3 hover:text-gold',
              ].join(' ')}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Favorites */}
      <div className="flex items-center pr-3.5 border-r border-loupe-border shrink-0">
        <button
          onClick={toggleFavorites}
          className={[
            'flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs min-h-[36px] transition-colors',
            'border',
            favorites === 'on'
              ? 'text-heart bg-heart/10 border-heart/30'
              : 'text-loupe-text-2 bg-loupe-bg-2 border-loupe-border hover:text-loupe-text',
          ].join(' ')}
        >
          <Heart
            className={`w-3.5 h-3.5 ${favorites === 'on' ? 'fill-heart' : ''}`}
            strokeWidth={2}
          />
          Favorites
        </button>
      </div>

      {/* By user — checkboxar (default ingen aktiv = "any") */}
      <div className="flex items-center gap-2 pr-3.5 border-r border-loupe-border shrink-0">
        <span className="hidden sm:inline text-[10px] tracking-[0.15em] uppercase text-loupe-text-3">
          By
        </span>
        <div className="flex gap-1.5">
          {BY_USERS.map((u) => {
            const active = u.name === 'henrik' ? henrikActive : michelleActive;
            return (
              <button
                key={u.name}
                onClick={() => toggleByUser(u.name)}
                title={
                  active
                    ? `Visa bara bilder ${u.label} markerat (klicka för att ta bort filtret)`
                    : `Filtrera på ${u.label}`
                }
                className={[
                  'px-3.5 py-2 text-xs min-h-[36px] rounded-lg transition-colors border',
                  active
                    ? u.activeClass
                    : 'bg-loupe-bg-2 text-loupe-text-2 border-loupe-border hover:text-loupe-text',
                ].join(' ')}
              >
                {u.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Spacer som puffar kolumn-toggle till höger på desktop */}
      <div className="hidden sm:flex flex-1" />

      {/* Column count */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="hidden sm:inline text-[10px] tracking-[0.15em] uppercase text-loupe-text-3">
          Cols
        </span>
        <div className="flex bg-loupe-bg-2 border border-loupe-border rounded-lg overflow-hidden">
          {([1, 2, 3, 4] as const).map((c) => (
            <button
              key={c}
              onClick={() => setColumns(c)}
              title={`${c} column${c === 1 ? '' : 's'}`}
              className={[
                'w-9 h-9 flex items-center justify-center transition-colors',
                columns === c
                  ? 'bg-white text-black'
                  : 'text-loupe-text-2 hover:text-loupe-text',
              ].join(' ')}
            >
              <ColumnIcon count={c} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
